import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ScenarioRow {
  readonly scenarioId: string;
  readonly scenarioCode: string;
  readonly scenarioName: string;
  readonly scenarioType: string;
  readonly runStatus: string;
  readonly lastRunAt: string | null;
  readonly status: string;
}

const SCENARIO_TYPES = ['WHAT_IF', 'BUDGET_SIMULATION', 'CAMPAIGN_SIMULATION', 'RESOURCE_SIMULATION', 'RISK_SIMULATION', 'APPROVAL_IMPACT'];
const PAGE = 25;

/** Parse a "metric=value, metric=value" string into a numeric baseline map. */
function parseBaseline(text: string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const pair of text.split(',')) {
    const [k, v] = pair.split('=').map((s) => s.trim());
    if (k && v !== undefined && v !== '' && Number.isFinite(Number(v))) out[k] = Number(v);
  }
  return out;
}

/** Parse a "metric=deltaPct, metric=deltaPct" string into assumption objects. */
function parseAssumptions(text: string): Array<{ metric: string; deltaPct: number }> {
  const out: Array<{ metric: string; deltaPct: number }> = [];
  for (const pair of text.split(',')) {
    const [k, v] = pair.split('=').map((s) => s.trim());
    if (k && v !== undefined && v !== '' && Number.isFinite(Number(v))) out.push({ metric: k, deltaPct: Number(v) });
  }
  return out;
}

/**
 * M41 — Scenario Modeling Studio (§26 / §12). The what-if simulation surface over
 * the rule-based scenario models (T541). Define a scenario across the six
 * simulation kinds with a numeric baseline and percentage assumptions, then run
 * it to project the baseline forward and compare. All simulations are rule-based
 * and advisory (§4 / §31 — no autonomous decisioning). Viewing needs
 * SCENARIO_VIEW, defining needs SCENARIO_MANAGE, running needs SCENARIO_RUN.
 */
export function ScenarioModelingStudio(): ReactNode {
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<ScenarioRow[]>([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [name, setName] = useState('');
  const [scenarioType, setScenarioType] = useState('WHAT_IF');
  const [baseline, setBaseline] = useState('');
  const [assumptions, setAssumptions] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (typeFilter) params.set('scenarioType', typeFilter);
    if (search) params.set('search', search);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: ScenarioRow[]; total: number }>(`/v1/data-lake/scenarios?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, typeFilter, search, page, reload]);

  const define = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(t('m41.scenario.nameRequired')); return; }
    const baselineConfig = parseBaseline(baseline);
    if (Object.keys(baselineConfig).length === 0) { setActionError(t('m41.scenario.baselineRequired')); return; }
    try {
      await apiRequest('/v1/data-lake/scenarios', {
        method: 'POST',
        body: { scenarioName: name.trim(), scenarioType, baselineConfig, assumptions: parseAssumptions(assumptions) },
        token,
      });
      setNotice(t('m41.scenario.defined'));
      setName('');
      setBaseline('');
      setAssumptions('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.scenario.notAuthDefine'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const run = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/data-lake/scenarios/${id}/run`, { method: 'POST', body: {}, token });
      setNotice(t('m41.scenario.simulated'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.scenario.notAuthRun'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="scenario-modeling-studio">
      <h1>{t('m41.scenario.title')}</h1>

      <section aria-label="scenario-define">
        <h2>{t('m41.scenario.define')}</h2>
        <form aria-label="define-form" onSubmit={(e) => void define(e)}>
          <label>{t('m41.common.name')}<input aria-label="field-scenario-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{t('m41.common.type')}
            <select aria-label="field-scenario-type" value={scenarioType} onChange={(e) => setScenarioType(e.target.value)}>
              {SCENARIO_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m41.scenario.baseline')}<input aria-label="field-baseline" placeholder="revenue=1000, cost=400" value={baseline} onChange={(e) => setBaseline(e.target.value)} /></label>
          <label>{t('m41.scenario.assumptions')}<input aria-label="field-assumptions" placeholder="revenue=10, cost=-5" value={assumptions} onChange={(e) => setAssumptions(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-define">{t('m41.common.define')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="scenario-action-error">{actionError}</p>}
        {notice && <p aria-label="scenario-notice">{notice}</p>}
      </section>

      <section aria-label="scenario-filters">
        <label>{t('m41.common.type')}
          <select aria-label="filter-scenario-type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            {SCENARIO_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('common.search')}<input aria-label="filter-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
      </section>

      {error && <p role="alert" aria-label="scenario-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="scenario-total">{t('m41.scenario.total', { count: total })}</p>
          <DataTable
            ariaLabel="scenario-table"
            rows={rows}
            rowKey={(s) => s.scenarioId}
            emptyMessage={t('m41.scenario.empty')}
            columns={[
              { key: 'code', header: t('m41.common.code'), cellAriaLabel: (s) => `scenario-code-${s.scenarioId}`, render: (s) => s.scenarioCode },
              { key: 'name', header: t('m41.common.name'), render: (s) => s.scenarioName },
              { key: 'type', header: t('m41.common.type'), render: (s) => s.scenarioType },
              { key: 'run', header: t('m41.scenario.runCol'), cellAriaLabel: (s) => `run-status-${s.scenarioId}`, render: (s) => s.runStatus },
              { key: 'lastRun', header: t('m41.scenario.lastRun'), render: (s) => at(s.lastRunAt) },
              { key: 'status', header: t('m41.common.status'), render: (s) => s.status },
              { key: 'simulate', header: t('m41.scenario.simulateCol'), render: (s) => (
                <Button variant="secondary" type="button" aria-label={`run-${s.scenarioId}`} onClick={() => void run(s.scenarioId)}>{t('m41.scenario.simulate')}</Button>
              ) },
            ]}
          />

          <nav aria-label="scenario-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m41.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m41.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m41.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
